import React, { useState } from 'react';
import { RotateCcw, ChevronUp, ChevronDown, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';

const PriorityMatrix: React.FC = () => {
  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: "Complete portfolio website",
      description: "Finish personal portfolio for job applications",
      urgency: "High",
      importance: "High",
      category: "Career",
      estimatedTime: 8,
      dueDate: "2025-08-15"
    },
    {
      id: 2,
      title: "Respond to client emails",
      description: "Reply to pending client inquiries",
      urgency: "High",
      importance: "Medium",
      category: "Work",
      estimatedTime: 1,
      dueDate: "2025-08-10"
    },
    {
      id: 3,
      title: "Learn advanced React patterns",
      description: "Study hooks, context, and performance optimization",
      urgency: "Low",
      importance: "High",
      category: "Learning",
      estimatedTime: 12,
      dueDate: "2025-09-01"
    },
    {
      id: 4,
      title: "Plan vacation itinerary",
      description: "Research and book activities for upcoming trip",
      urgency: "Medium",
      importance: "Low",
      category: "Personal",
      estimatedTime: 3,
      dueDate: "2025-08-25"
    },
    {
      id: 5,
      title: "Social media scrolling",
      description: "Checking social media feeds mindlessly",
      urgency: "Low",
      importance: "Low",
      category: "Distraction",
      estimatedTime: 2,
      dueDate: ""
    },
    {
      id: 6,
      title: "Emergency bug fix",
      description: "Fix critical production issue affecting users",
      urgency: "High",
      importance: "High",
      category: "Work",
      estimatedTime: 4,
      dueDate: "2025-08-10"
    }
  ]);

  const getQuadrant = (urgency: string, importance: string) => {
    if (urgency === 'High' && importance === 'High') return 'urgent-important';
    if (urgency === 'Low' && importance === 'High') return 'not-urgent-important';
    if (urgency === 'High' && importance === 'Medium') return 'urgent-not-important';
    return 'not-urgent-not-important';
  };

  const getQuadrantInfo = (quadrant: string) => {
    switch (quadrant) {
      case 'urgent-important':
        return {
          title: 'Do First',
          subtitle: 'Urgent & Important',
          color: 'bg-red-50 border-red-200',
          textColor: 'text-red-900',
          description: 'Crisis management, emergencies, deadline-driven projects'
        };
      case 'not-urgent-important':
        return {
          title: 'Schedule',
          subtitle: 'Not Urgent & Important',
          color: 'bg-green-50 border-green-200',
          textColor: 'text-green-900',
          description: 'Long-term goals, planning, prevention, personal development'
        };
      case 'urgent-not-important':
        return {
          title: 'Delegate',
          subtitle: 'Urgent & Not Important',
          color: 'bg-yellow-50 border-yellow-200',
          textColor: 'text-yellow-900',
          description: 'Interruptions, some emails, some meetings, some calls'
        };
      case 'not-urgent-not-important':
        return {
          title: 'Eliminate',
          subtitle: 'Not Urgent & Not Important',
          color: 'bg-gray-50 border-gray-200',
          textColor: 'text-[color:var(--lyra-color-text)]',
          description: 'Time wasters, some social media, excessive TV, busy work'
        };
      default:
        return {
          title: 'Unknown',
          subtitle: '',
          color: 'bg-gray-50 border-gray-200',
          textColor: 'text-[color:var(--lyra-color-text)]',
          description: ''
        };
    }
  };

  const quadrants = [
    'urgent-important',
    'not-urgent-important',
    'urgent-not-important',
    'not-urgent-not-important'
  ];

  const getTasksByQuadrant = (quadrant: string) => {
    return tasks.filter(task => getQuadrant(task.urgency, task.importance) === quadrant);
  };

  const moveTask = (taskId: number, direction: 'up' | 'down', axis: 'urgency' | 'importance') => {
    setTasks(tasks.map(task => {
      if (task.id === taskId) {
        if (axis === 'urgency') {
          const newUrgency = direction === 'up' 
            ? (task.urgency === 'Low' ? 'Medium' : task.urgency === 'Medium' ? 'High' : 'High')
            : (task.urgency === 'High' ? 'Medium' : task.urgency === 'Medium' ? 'Low' : 'Low');
          return { ...task, urgency: newUrgency };
        } else {
          const newImportance = direction === 'up'
            ? (task.importance === 'Low' ? 'Medium' : task.importance === 'Medium' ? 'High' : 'High')
            : (task.importance === 'High' ? 'Medium' : task.importance === 'Medium' ? 'Low' : 'Low');
          return { ...task, importance: newImportance };
        }
      }
      return task;
    }));
  };

  return (
    <div className="p-6 max-w-7xl mx-auto project-mgmt-page">
      <PageTracker pageTitle="Priority Matrix" category="project-management" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[color:var(--lyra-color-text)] mb-2">Priority Matrix</h1>
        <p className="text-[color:var(--app-text-2)] text-lg">
          Organize tasks using the Eisenhower Matrix. Categorize activities by urgency and importance 
          to focus on what truly matters and eliminate time-wasting activities.
        </p>
      </div>

      <div className="mb-6 bg-[color:var(--app-primary-soft)] border border-[color:var(--app-primary-soft-2)] rounded-lg p-4">
        <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-2">How to Use the Priority Matrix</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="text-red-800">
            <strong>Do First:</strong> Handle immediately
          </div>
          <div className="text-green-800">
            <strong>Schedule:</strong> Plan for later
          </div>
          <div className="text-yellow-800">
            <strong>Delegate:</strong> Assign to others
          </div>
          <div className="text-[color:var(--lyra-color-text)]">
            <strong>Eliminate:</strong> Remove or minimize
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {quadrants.map((quadrant) => {
          const quadrantInfo = getQuadrantInfo(quadrant);
          const quadrantTasks = getTasksByQuadrant(quadrant);
          
          return (
            <div key={quadrant} className={`rounded-lg border-2 ${quadrantInfo.color} p-6`}>
              <div className="mb-4">
                <h2 className={`text-xl font-bold ${quadrantInfo.textColor}`}>
                  {quadrantInfo.title}
                </h2>
                <p className={`text-sm font-medium ${quadrantInfo.textColor} opacity-80`}>
                  {quadrantInfo.subtitle}
                </p>
                <p className={`text-xs ${quadrantInfo.textColor} opacity-60 mt-1`}>
                  {quadrantInfo.description}
                </p>
              </div>
              
              <div className="space-y-3">
                {quadrantTasks.map((task) => (
                  <div key={task.id} className="bg-white rounded-lg border border-[color:var(--lyra-color-border)] p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-[color:var(--lyra-color-text)] text-sm">{task.title}</h3>
                      <div className="flex space-x-1">
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveTask(task.id, 'up', 'importance')}
                            className="text-[color:var(--app-text-3)] hover:text-[color:var(--app-primary-mid)] p-1"
                            title="Increase importance"
                          >
                            <ChevronUp size={12} />
                          </button>
                          <button
                            onClick={() => moveTask(task.id, 'down', 'importance')}
                            className="text-[color:var(--app-text-3)] hover:text-[color:var(--app-primary-mid)] p-1"
                            title="Decrease importance"
                          >
                            <ChevronDown size={12} />
                          </button>
                        </div>
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveTask(task.id, 'up', 'urgency')}
                            className="text-[color:var(--app-text-3)] hover:text-red-600 p-1"
                            title="Increase urgency"
                          >
                            <ChevronUp size={12} />
                          </button>
                          <button
                            onClick={() => moveTask(task.id, 'down', 'urgency')}
                            className="text-[color:var(--app-text-3)] hover:text-red-600 p-1"
                            title="Decrease urgency"
                          >
                            <ChevronDown size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-[color:var(--app-text-2)] text-xs mb-3">{task.description}</p>
                    
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        <span className="bg-[color:var(--lyra-color-surface-2)] text-[#4A4A47] px-2 py-1 rounded">
                          {task.category}
                        </span>
                        <div className="flex items-center text-[color:var(--app-text-3)]">
                          <Clock size={10} className="mr-1" />
                          <span>{task.estimatedTime}h</span>
                        </div>
                      </div>
                      
                      {task.dueDate && (
                        <div className={`flex items-center ${
                          new Date(task.dueDate) < new Date() 
                            ? 'text-red-600' 
                            : new Date(task.dueDate).getTime() - new Date().getTime() < 3 * 24 * 60 * 60 * 1000
                            ? 'text-yellow-600'
                            : 'text-[color:var(--app-text-3)]'
                        }`}>
                          {new Date(task.dueDate) < new Date() ? (
                            <AlertTriangle size={10} className="mr-1" />
                          ) : (
                            <CheckCircle size={10} className="mr-1" />
                          )}
                          <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {quadrantTasks.length === 0 && (
                  <div className="bg-white border-2 border-dashed border-[color:var(--app-border-strong)] rounded-lg p-6 text-center text-[color:var(--app-text-3)]">
                    <span className="text-sm">No tasks in this quadrant</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[color:var(--lyra-color-border)] rounded-lg p-4">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-2">Quick Stats</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[color:var(--app-text-2)]">Total Tasks:</span>
              <span className="font-medium">{tasks.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-600">Critical:</span>
              <span className="font-medium">{getTasksByQuadrant('urgent-important').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-600">Important:</span>
              <span className="font-medium">{getTasksByQuadrant('not-urgent-important').length}</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-[color:var(--lyra-color-border)] rounded-lg p-4">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-2">Time Distribution</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[color:var(--app-text-2)]">Do First:</span>
              <span className="font-medium">
                {getTasksByQuadrant('urgent-important').reduce((acc, task) => acc + task.estimatedTime, 0)}h
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--app-text-2)]">Schedule:</span>
              <span className="font-medium">
                {getTasksByQuadrant('not-urgent-important').reduce((acc, task) => acc + task.estimatedTime, 0)}h
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[color:var(--app-text-2)]">Delegate:</span>
              <span className="font-medium">
                {getTasksByQuadrant('urgent-not-important').reduce((acc, task) => acc + task.estimatedTime, 0)}h
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-[color:var(--lyra-color-border)] rounded-lg p-4">
          <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)] mb-2">Recommendations</h3>
          <div className="space-y-2 text-xs text-[color:var(--app-text-2)]">
            <p>• Focus 60% time on "Schedule" quadrant</p>
            <p>• Minimize "Do First" to 25% or less</p>
            <p>• Delegate "Urgent & Not Important"</p>
            <p>• Eliminate time wasters completely</p>
          </div>
        </div>
        
        <div className="bg-[color:var(--app-primary-soft)] border border-[color:var(--lyra-color-border)] rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <RotateCcw className="text-[color:var(--app-primary-mid)]" size={20} />
            <h3 className="text-lg font-semibold text-[color:var(--lyra-color-text)]">Weekly Review</h3>
          </div>
          <p className="text-[color:var(--app-primary-deep)] text-xs">
            Review and reorganize your matrix weekly to maintain focus on what truly matters.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriorityMatrix;
